import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, Observable, of, tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApiResponse, unwrap } from '../models/api.types';
import { Admin, LoginRequest, LoginResponse } from '../models/auth.types';

const TOKEN_KEY = 'pals.admin.token';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private readonly _token = signal<string | null>(this.readToken());
  private readonly _admin = signal<Admin | null>(null);

  readonly token = this._token.asReadonly();
  readonly admin = this._admin.asReadonly();
  readonly isAuthenticated = computed(() => !!this._token());

  login(creds: LoginRequest): Observable<LoginResponse> {
    return this.http
      .post<ApiResponse<LoginResponse>>(`${environment.apiBaseUrl}/auth/login`, creds)
      .pipe(
        unwrap(),
        tap((res) => {
          this.setToken(res.token);
          this._admin.set(res.admin);
        }),
      );
  }

  /** Called from the shell on boot — validates the stored token. */
  restoreSession(): Observable<Admin | null> {
    if (!this._token()) return of(null);
    return this.me().pipe(
      catchError(() => {
        this.clearLocal();
        return of(null);
      }),
    );
  }

  /**
   * Fetch the latest admin profile from /auth/me. Refreshes the cached
   * admin signal so the header chip and profile page stay in sync.
   */
  me(): Observable<Admin> {
    return this.http
      .get<ApiResponse<Admin>>(`${environment.apiBaseUrl}/auth/me`)
      .pipe(
        unwrap(),
        tap((admin) => this._admin.set(admin)),
      );
  }

  logout(): void {
    // Fire-and-forget server-side revoke; clear local state immediately.
    if (this._token()) {
      this.http
        .post(`${environment.apiBaseUrl}/auth/logout`, null)
        .pipe(
          catchError(() => of(null)),
          finalize(() => this.clearLocal()),
        )
        .subscribe();
    } else {
      this.clearLocal();
    }
  }

  /** Used by the auth interceptor on a 401 — no server call. */
  forceLogout(): void {
    this.clearLocal();
  }

  private clearLocal(): void {
    this._token.set(null);
    this._admin.set(null);
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      // ignore
    }
    void this.router.navigate(['/login']);
  }

  private setToken(token: string): void {
    this._token.set(token);
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      // ignore
    }
  }

  private readToken(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  }
}
